import {
  useState,
  ReactElement,
  cloneElement,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import React from "react";
import {
  ChevronLeft,
  ChevronRight,
} from "@anu-its/ts-react-components/icons/simple";
import { Button } from "@anu-its/ts-react-components";

interface TablePaginationProps {
  children: ReactElement<{
    page: number;
    pageSize: number;
    resetPage: () => void;
    handleTotalResults: (totalResults: number) => void;
    showNextButton: (isNextButton: boolean) => void;
  }>;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  children,
}) => {
  const initialPage = 1;
  const pageSize = 20;

  const [page, setPage] = useState<number>(initialPage);
  const [totalResults, setTotalResults] = useState<number>(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const isNext = useMemo(() => {
    return page * pageSize < totalResults;
  }, [page, pageSize, totalResults]);

  const handleTotalResults = useCallback((newTotalResults: number) => {
    setTotalResults(newTotalResults);
  }, []);

  const resetPage = () => {
    setPage(1);
  };

  const clonedChild = useMemo(() => {
    return cloneElement(children, {
      page,
      pageSize,
      resetPage,
      handleTotalResults,
    });
  }, [children, page, pageSize, handleTotalResults]);

  return (
    <>
      {/* Table */}
      {clonedChild}

      {/* Pagination */}
      <div
        className={`mt-5 w-full flex items-center ${totalResults > 0 ? "justify-between" : "justify-end"}  gap-6`}
      >
        {totalResults > 0 && (
          <div>
            Total Results: <span className="font-bold">{totalResults}</span>
          </div>
        )}
        <div className="flex gap-6 items-center">
          <div>
            Page <span className="font-bold">{page}</span>
          </div>
          <div className="flex gap-6">
            <Button
              aria-label="Go to previous page"
              classNames="p-2 rounded-full !border-2 !border-black"
              onClick={() => setPage((prev) => (prev > 1 ? prev - 1 : 1))}
              disabled={page === 1}
            >
              <ChevronLeft />
            </Button>

            <Button
              aria-label="Go to next page"
              classNames="p-2 rounded-full !border-2 !border-black"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={!isNext}
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
